import { Grid, InputAdornment, TextField, Tooltip } from '@material-ui/core';
import { AccountBalance, AccountTree, ArrowDownward, ArrowUpward, CloudDownload, DateRange, Event, FilterList, HourglassEmpty, MonetizationOn, PersonAdd, PersonAddDisabled, RedoTwoTone, Search } from '@material-ui/icons';
import moment from 'moment';
import React, { useState } from 'react';
import * as Toastr from "toastr";
import { downloadActiveIDReport, downloadInActiveIDReport, downloadPayoutReport, downloadPendingPayoutReport, downloadReTopCommissionReport, downloadReTopReport, downloadRefReport, downloadReferralReport, downloadRewardReport, downloadSwapReport, downloadWithdrawReport } from '../../helpers/PDFGenerator';
import MemberAPI from '../../store/member/member.api';
import TransactionAPI from '../../store/transaction/transaction.api';
import Loader from '../base/loader';

const CATEGORIES = ["All", "Members", "Transactions", "Commissions"];

// requires: "dates"    -> From and To date must both be supplied (the default)
//           "memberId" -> a Member ID must be supplied; dates do not apply
const REPORTS = [
    {
        key: "active-id",
        title: "Active ID Report",
        subtitle: "Members with an active plan",
        category: "Members",
        tone: "green",
        requires: "dates",
        icon: <PersonAdd />,
        load: (filter, note) => MemberAPI.GetMembers(filter)
            .then((t) => downloadActiveIDReport(t.members || [], note)),
    },
    {
        key: "inactive-id",
        title: "In-active ID Report",
        subtitle: "Members yet to activate",
        category: "Members",
        tone: "amber",
        requires: "dates",
        icon: <PersonAddDisabled />,
        load: (filter, note) => MemberAPI.GetMembers(filter)
            .then((t) => downloadInActiveIDReport(t.members || [], note)),
    },
    {
        key: "referral",
        title: "Referral Report",
        subtitle: "Full downline of one member",
        category: "Members",
        tone: "blue",
        requires: "memberId",
        icon: <AccountTree />,
        load: (filter, note) => MemberAPI.GetCurrentUserReferals(filter.memberId)
            .then((res) => {
                const root = Array.isArray(res) && res.length > 0 ? res[0] : null;
                const children = (root && root.children) || [];
                if (!root) {
                    Toastr.warning(`No member found for ${filter.memberId}`);
                    return;
                }
                if (children.length === 0) {
                    Toastr.info(`${filter.memberId} has no referrals yet`);
                    return;
                }
                downloadReferralReport(children, note);
            }),
    },
    {
        key: "payout",
        title: "Payout Report",
        subtitle: "All Users",
        category: "Transactions",
        tone: "blue",
        requires: "dates",
        icon: <MonetizationOn />,
        load: (filter, note) => TransactionAPI.GetAllPayouts(filter)
            .then((t) => downloadPayoutReport(t.payouts || [], note)),
    },
    {
        key: "payout-pending",
        title: "Payout Pending Report",
        subtitle: "Created payouts, per member",
        category: "Transactions",
        tone: "amber",
        requires: "dates",
        icon: <HourglassEmpty />,
        load: (filter, note) => TransactionAPI.GetAllPayouts({ ...filter, status: "Created" })
            .then((t) => downloadPendingPayoutReport(t.payouts || [], note)),
    },
    {
        key: "withdraw",
        title: "Withdraw Report",
        subtitle: "Approved Withdraws",
        category: "Transactions",
        tone: "violet",
        requires: "dates",
        icon: <AccountBalance />,
        load: (filter, note) => TransactionAPI.GetWithdrawTransactions({ ...filter, status: "Approved" })
            .then((s) => downloadWithdrawReport(s.withdraws || [], note)),
    },
    {
        key: "buy",
        title: "Buy Report",
        subtitle: "All Buy Transactions",
        category: "Transactions",
        tone: "green",
        requires: "dates",
        icon: <ArrowUpward />,
        load: (filter, note) => TransactionAPI.GetBuyTransactions({ ...filter, status: "Approved" })
            .then((s) => downloadSwapReport(s || [], "Buy Transaction Report", note)),
    },
    {
        key: "sell",
        title: "Sell Report",
        subtitle: "All Sell Transactions",
        category: "Transactions",
        tone: "red",
        requires: "dates",
        icon: <ArrowDownward />,
        load: (filter, note) => TransactionAPI.GetSellTransactions({ ...filter, status: "Approved" })
            .then((s) => downloadSwapReport(s || [], "Sell Transaction Report", note)),
    },
    {
        key: "rewards",
        title: "Rewards Report",
        subtitle: "All Levels",
        category: "Commissions",
        tone: "amber",
        requires: "dates",
        icon: <DateRange />,
        load: (filter, note) => TransactionAPI.GetAllPayouts(filter)
            .then((s) => downloadRewardReport(s.payouts || [], note)),
    },
    {
        key: "retopup",
        title: "Retopup Report",
        subtitle: "All Levels",
        category: "Commissions",
        tone: "blue",
        requires: "dates",
        icon: <Event />,
        load: (filter, note) => TransactionAPI.GetAllPayouts(filter)
            .then((s) => downloadReTopReport(s.payouts || [], note)),
    },
    {
        key: "referral-commission",
        title: "Referal commission",
        subtitle: "All Levels",
        category: "Commissions",
        tone: "violet",
        requires: "dates",
        icon: <AccountTree />,
        load: (filter, note) => TransactionAPI.GetAllPayouts(filter)
            .then((s) => downloadRefReport(s.payouts || [], note)),
    },
    {
        key: "retopup-commission",
        title: "Retopup commission",
        subtitle: "All Levels",
        category: "Commissions",
        tone: "green",
        requires: "dates",
        icon: <RedoTwoTone />,
        load: (filter, note) => TransactionAPI.GetAllPayouts(filter)
            .then((s) => downloadReTopCommissionReport(s.payouts || [], note)),
    },
];

export default function ReportContainer() {
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState("All");
    const [query, setQuery] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [memberId, setMemberId] = useState("");
    const [attempted, setAttempted] = useState(false);

    const trimmedMemberId = memberId.trim();
    const isRangeInvalid = !!fromDate && !!toDate && moment(toDate).isBefore(moment(fromDate), "day");

    /** Returns the reason a report cannot be downloaded yet, or null when it can. */
    const blockedReason = (report) => {
        if (report.requires === "memberId") {
            return trimmedMemberId ? null : "Enter a Member ID to download this report";
        }
        if (!fromDate && !toDate) return "From and To dates are required";
        if (!fromDate) return "From date is required";
        if (!toDate) return "To date is required";
        if (isRangeInvalid) return "To date must be on or after the From date";
        return null;
    };

    /** Dates are not applicable to member-scoped reports, so they are not sent. */
    const filterFor = (report) => (report.requires === "memberId"
        ? { memberId: trimmedMemberId }
        : {
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
            memberId: trimmedMemberId || undefined,
        });

    const noteFor = (report) => {
        const applied = filterFor(report);
        const parts = [];
        if (applied.fromDate) parts.push(`From ${moment(applied.fromDate).format("ll")}`);
        if (applied.toDate) parts.push(`To ${moment(applied.toDate).format("ll")}`);
        if (applied.memberId) parts.push(`Member ${applied.memberId}`);
        return parts.length ? `Filters — ${parts.join("  ·  ")}` : "";
    };

    const clearFilters = () => {
        setFromDate("");
        setToDate("");
        setMemberId("");
        setAttempted(false);
    };

    const runReport = (report) => {
        const reason = blockedReason(report);
        if (reason) {
            setAttempted(true);
            Toastr.warning(reason);
            return;
        }
        setLoading(true);
        report.load(filterFor(report), noteFor(report)).finally(() => setLoading(false));
    };

    const dateError = attempted && (!fromDate || !toDate);
    const memberError = attempted && !trimmedMemberId;
    const activeFilters = [fromDate, toDate, trimmedMemberId].filter(Boolean).length;

    const search = query.trim().toLowerCase();
    const visibleReports = REPORTS.filter((report) => {
        const matchesCategory = category === "All" || report.category === category;
        const matchesSearch = !search
            || report.title.toLowerCase().indexOf(search) > -1
            || report.subtitle.toLowerCase().indexOf(search) > -1
            || report.category.toLowerCase().indexOf(search) > -1;
        return matchesCategory && matchesSearch;
    });

    const countFor = (name) => name === "All"
        ? REPORTS.length
        : REPORTS.filter((report) => report.category === name).length;

    return (
        <div className='reports-page'>
            {
                loading && <Loader />
            }
            <div className="reports-intro">
                <div className="reports-intro-copy">
                    <span className="reports-kicker">Export center</span>
                    <h1>Reports &amp; insights</h1>
                    <p>Download operational reports for members, transactions, payouts, and commissions.</p>
                </div>
                <div className="reports-count">
                    <strong>{REPORTS.length}</strong>
                    <span>Report types</span>
                </div>
            </div>

            <div className="reports-filterbar">
                <div className="reports-filterbar-head">
                    <span className="reports-filterbar-title"><FilterList />Report filters</span>
                    {
                        activeFilters > 0 &&
                        <span className="reports-filterbar-actions">
                            <span className="reports-filter-count">{activeFilters} active</span>
                            <button type="button" className="reports-filter-clear" onClick={clearFilters}>Clear</button>
                        </span>
                    }
                </div>
                <Grid container className="reports-filter-fields">
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                        <TextField
                            label="From Date *"
                            type="date"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            error={attempted && !fromDate}
                            helperText="Required for every report except Referral"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                        <TextField
                            label="To Date *"
                            type="date"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            error={isRangeInvalid || (attempted && !toDate)}
                            helperText={isRangeInvalid
                                ? "To date must be on or after the From date"
                                : "Required for every report except Referral"}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                        <TextField
                            label="Member ID"
                            placeholder="e.g. T369001"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            value={memberId}
                            onChange={(e) => setMemberId(e.target.value)}
                            error={memberError}
                            helperText="Optional filter — required for the Referral report"
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><PersonAdd /></InputAdornment>
                            }}
                        />
                    </Grid>
                </Grid>
                <span className={`reports-filterbar-note ${dateError || isRangeInvalid ? "is-error" : ""}`}>
                    {
                        dateError || isRangeInvalid
                            ? "Set a valid From and To date to download the date based reports."
                            : activeFilters > 0
                                ? `Reports are generated with: ${noteFor({ requires: "dates" }).replace("Filters — ", "")}`
                                : "Pick a date range to enable the reports below."
                    }
                </span>
            </div>

            <div className="reports-toolbar">
                <div className="reports-filters">
                    {
                        CATEGORIES.map((name) => (
                            <button
                                type="button"
                                key={name}
                                className={`reports-filter ${category === name ? "is-active" : ""}`}
                                onClick={() => setCategory(name)}
                            >
                                {name}
                                <span>{countFor(name)}</span>
                            </button>
                        ))
                    }
                </div>
                <TextField
                    className="reports-search"
                    variant="outlined"
                    margin="dense"
                    placeholder="Search reports"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
                    }}
                />
            </div>

            <Grid container className="reports-grid">
                {
                    visibleReports.map((report) => {
                        const reason = blockedReason(report);
                        return (
                            <Grid item xs={12} sm={12} md={6} lg={4} key={report.key}>
                                <Tooltip title={reason || "Download report"}>
                                    <div
                                        className={`report-card report-card-${report.tone} ${reason ? "is-disabled" : ""}`}
                                        role="button"
                                        tabIndex={reason ? -1 : 0}
                                        onClick={() => runReport(report)}
                                        onKeyDown={(e) => { if (e.key === "Enter") runReport(report); }}
                                    >
                                        <span className="report-card-icon">{report.icon}</span>
                                        <div className="report-card-body">
                                            <strong>{report.title}</strong>
                                            <span className="report-card-sub">{report.subtitle}</span>
                                            <span className={`report-card-tag ${report.requires === "memberId" ? "is-required" : ""}`}>
                                                {report.requires === "memberId" ? "Needs Member ID" : report.category}
                                            </span>
                                        </div>
                                        <span className="report-card-download"><CloudDownload /></span>
                                    </div>
                                </Tooltip>
                            </Grid>
                        );
                    })
                }
                {
                    visibleReports.length === 0 &&
                    <Grid item xs={12}>
                        <div className="reports-empty">
                            <Search />
                            <strong>No reports match your filter</strong>
                            <span>Try a different keyword or switch the category.</span>
                        </div>
                    </Grid>
                }
            </Grid>
        </div>
    );
}
